import Page from '@/components/custom/Page'
import { WorkspaceLayout } from '@/features/workspace'

import { useSpeechToText } from '@/features/screenplay/components/v2s/stt'
import {
    V2SElementType,
    v2SElementTypeMetaData,
} from '@/features/screenplay/components/v2s/v2s-elements'
import { V2SContextProvider, useV2SContext } from '@/features/screenplay/components/v2s/V2SProvider'
import Waveform from '@/features/screenplay/components/v2s/WaveForm'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { ToggleGroup, ToggleGroupItem } from '@/shared/components/ui/toggle-group'
import { Check, Mic, Pause, Play, StopCircle, Trash2 } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { SpEditorProvider } from '@/features/screenplay/components/editor/SpEditorProvider'
import V2SOptionbar from '@/features/screenplay/components/v2s/v2s-optionbar'
import { useRemoteConfigStore } from '@/features/shared/stores/config.store'
import V2SInterestPage from './V2SInterestPage'

export default function V2SPage() {
    const enableV2S = useRemoteConfigStore((state) => state.enableV2S)
    const ProviderWrapper = ({ children }: { children: React.ReactNode }) => (
        <SpEditorProvider>
            <V2SContextProvider>{children}</V2SContextProvider>
        </SpEditorProvider>
    )

    if (!enableV2S) {
        return <V2SInterestPage />
    }

    return (
        <WorkspaceLayout ProviderWrapper={ProviderWrapper} optionBar={<V2SOptionbar />}>
            <V2SPageContent />
        </WorkspaceLayout>
    )
}

const V2SPageContent = () => {
    const { project } = useWorkspace()
    return (
        <Page className="bg-primary">
            <div className="h-full">
                <AudioChat />
            </div>
        </Page>
    )
}

type RecordingState = 'idle' | 'recording' | 'paused'

export type VoiceElement = {
    audioBlob: Blob
    type: V2SElementType
}
export type V2SElement = VoiceElement & {
    projectId: string
    userId: string
    created_at: Date
    updated_at: Date
    duration: number
    id: string
}
export function AudioChat() {
    const [recordingState, setRecordingState] = useState<RecordingState>('idle')
    const [v2sElement, setV2SElement] = useState<V2SElementType | undefined>(undefined)
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const audioChunks = useRef<Blob[]>([])
    //const [addedAudios, setAddedAudios] = useState<VoiceElement[]>([])
    const { addedAudios, addVoiceElement, removeVoiceElement } = useV2SContext()
    const lastAudioRef = useRef<HTMLDivElement>(null)
    const elements = Object.entries(v2SElementTypeMetaData)

    useEffect(() => {
        if (lastAudioRef.current) {
            lastAudioRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [addedAudios])

    const groupByScene = (elements: V2SElement[]) => {
        const groups: V2SElement[][] = []
        let currentGroup: V2SElement[] = []

        for (let i = 0; i < elements.length; i++) {
            const el = elements[i]

            if (el.type === 'scene') {
                // If there's an existing group, push it
                if (currentGroup.length > 0) {
                    groups.push(currentGroup)
                }
                // Start a new group with the current scene
                currentGroup = [el]
            } else {
                currentGroup.push(el)
            }
        }

        // Push the final group
        if (currentGroup.length > 0) {
            groups.push(currentGroup)
        }

        return groups
    }
    const { transcript, isRecording, start, stop } = useSpeechToText()

    const groupedAudios = groupByScene(addedAudios)

    const startRecording = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const mediaRecorder = new MediaRecorder(stream)
        mediaRecorderRef.current = mediaRecorder
        audioChunks.current = []
        mediaRecorder.start()
        start()

        mediaRecorder.ondataavailable = (event) => {
            audioChunks.current.push(event.data)
        }

        mediaRecorder.onstop = () => {
            const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' })
            setAudioBlob(audioBlob)
        }

        setRecordingState('recording')
    }

    const pauseRecording = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.pause()
            setRecordingState('paused')
        }
    }

    const resumeRecording = () => {
        if (mediaRecorderRef.current?.state === 'paused') {
            mediaRecorderRef.current.resume()
            setRecordingState('recording')
        }
    }

    const stopRecording = () => {
        if (mediaRecorderRef.current?.state !== 'inactive') {
            mediaRecorderRef.current?.stop()
        }
        setRecordingState('idle')
        stop()
    }

    const deleteRecording = () => {
        setAudioBlob(null)
    }

    const handleAddAudio = async () => {
        if (audioBlob) {
            const audioElement = { audioBlob: audioBlob, type: v2sElement! }
            addVoiceElement(audioElement)
            setAudioBlob(null)
            setV2SElement(undefined)
        }
    }

    const handlePlayPause = () => { }

    const handleReplay = () => { }
    const elementMeta = v2SElementTypeMetaData[v2sElement!]
    return (
        <div className="flex flex-col max-w-2x mx-auto relative  min-h-full">
            {/* Scrollable list */}
            {AudioScrollList()}
            {/* Fixed preview and controls at bottom */}
            <div className="sticky bottom-0 z-50 bg-primary mx-4">
                {RecordedAudioCard()}
                {/* Bottom Record Controls */}

                {v2sElement && !audioBlob && (
                    <Card className="rounded-2xl shadow-2xl bg-gradient-to-br from-white to-slate-50">
                        <CardContent className="px-4 space-y-2">
                            <Badge
                                variant="outline"
                                className="text-blue-600 border-blue-300 bg-blue-50"
                            >
                                {elementMeta.label}
                            </Badge>
                            {recordingState === 'recording' ? (
                                <div>{transcript}</div>
                            ) : (
                                v2sElement && !audioBlob && ElementHelperBox()
                            )}
                        </CardContent>
                    </Card>
                )}
                <div className="flex items-center justify-between gap-4 my-4 p-2 rounded-2xl shadow bg-white">
                    {ElementTypeToggleButtons()}
                    {v2sElement && RecordControlButtons()}
                </div>
            </div>
        </div>
    )

    function ElementHelperBox() {
        const meta = v2SElementTypeMetaData[v2sElement!]
        return (
            <>
                {/* <div className="flex items-center gap-3">
                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                        <h2 className="text-lg font-semibold tracking-tight">How to use Scene</h2>
                    </div> */}
                <div className="text-sm text-gray-700 leading-relaxed">{meta.helpText}</div>
                <div className="bg-muted/30 p-4 rounded-md border text-sm font-mono">
                    <span className="text-muted-foreground block mb-1">
                        🎙️ Example voice input:
                    </span>
                    {meta.ex}
                </div>
            </>
        )
        return <div className="bg-red-100">{v2SElementTypeMetaData[v2sElement!].helpText}</div>
    }

    function RecordedAudioCard(): React.ReactNode {
        if (!audioBlob) return <></>
        return (
            <Card className="p-4 m-4 space-y-4 ">
                <div className="space-y-2">
                    <div className="flex items-center">
                        <Waveform
                            voiceElement={{
                                audioBlob: audioBlob,
                                type: v2sElement!,
                            }}
                        />
                        <Button className="w-12 h-12 mx-2" size={'icon'} onClick={deleteRecording}>
                            <Trash2 className="w-5 h-5" />
                        </Button>
                        <Button
                            className="w-12 h-12 bg-emerald-600 hover:bg-emerald-500"
                            size={'icon'}
                            onClick={handleAddAudio}
                        >
                            <Check className="w-4 h-4" />
                        </Button>
                    </div>

                    {/*   <div ref={waveformRef} className="rounded-md" /> */}
                    <div className="flex space-x-2"></div>
                </div>
            </Card>
        )
    }

    function AudioScrollList() {
        const meta = v2SElementTypeMetaData['scene']
        if (groupedAudios.length === 0) {
            return (
                <div className="flex-1 bg-[--primary] text-white flex flex-col items-center justify-center p-6 rounded-xl space-y-4 shadow-lg">
                    <h2 className="text-2xl font-semibold text-center">
                        🎬 Start your story with a scene
                    </h2>
                    <p className="text-center text-base">
                        Tap <span className="font-bold underline">Scene</span> from the bar below to
                        begin. Once selected, describe your first scene in your own words.
                    </p>
                    <div className="italic text-center bg-white/10 p-4 rounded-md max-w-md">
                        {meta.ex}
                    </div>
                    <p className="text-center">Let your voice set the stage.</p>
                </div>
            )
        }
        return (
            <div className="flex-1 overflow-y-auto p-4 space-y-4 w-[90%] ml-auto">
                {groupedAudios.map((group, groupIdx) => (
                    <div
                        key={groupIdx}
                        className="border border-white/25 p-4 rounded-2xl space-y-4 bg-white/10"
                    >
                        {group.map((v2SElement, idx) => (
                            <div key={idx} className="p-4 flex items-center justify-between">
                                <Waveform
                                    voiceElement={v2SElement}
                                    onDelete={() => removeVoiceElement(v2SElement)}
                                />
                            </div>
                        ))}
                    </div>
                ))}
                <div ref={lastAudioRef} />
            </div>
        )
    }

    function ElementTypeToggleButtons() {
        return (
            <div className="text-muted-foreground text-sm flex-1 ">
                <ToggleGroup
                    type="single"
                    size="lg"
                    value={v2sElement ?? ''}
                    onValueChange={(value) => {
                        setV2SElement((value as V2SElementType) || undefined)
                    }}
                >
                    {elements.map(([key, meta], index) => {
                        const Icon = meta.icon
                        const isLast = index === elements.length - 1

                        return (
                            <React.Fragment key={key}>
                                <ToggleGroupItem
                                    className="data-[state=on]:bg-slate-300 data-[state=on]:rounded-lg"
                                    value={key}
                                    aria-label={`Toggle ${meta.label}`}
                                >
                                    <div className="flex items-center">
                                        <Icon className="size-7 md:size-8 xl:hidden" />
                                        <span className="h-4 px-5 hidden xl:block">
                                            {meta.label}
                                        </span>
                                    </div>
                                </ToggleGroupItem>

                                {!isLast && <div className="w-px h-8 bg-border mx-2" />}
                            </React.Fragment>
                        )
                    })}
                </ToggleGroup>
            </div>
        )
    }

    function RecordControlButtons() {
        return (
            <div className="relative w-[120px] flex items-center justify-end gap-2 ">
                {/* Record button */}
                {recordingState === 'idle' && (
                    <Button
                        onClick={startRecording}
                        className="rounded-full fade-in absolute right-0"
                        size="icon"
                    >
                        <Mic className="w-6 h-6" />
                    </Button>
                )}
                {/* Stop + Pause/Play during recording/paused */}
                {(recordingState === 'recording' || recordingState === 'paused') && (
                    <>
                        {/* Pause/Play button */}
                        <div
                            className={`absolute left-0 ${recordingState === 'recording' ? 'roll-in' : 'roll-out'
                                }`}
                        >
                            {recordingState === 'recording' ? (
                                <Button
                                    onClick={pauseRecording}
                                    variant="secondary"
                                    className="rounded-full"
                                    size="icon"
                                >
                                    <Pause className="w-6 h-6" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={resumeRecording}
                                    variant="secondary"
                                    className="rounded-full"
                                    size="icon"
                                >
                                    <Play className="w-6 h-6" />
                                </Button>
                            )}
                        </div>

                        <Button
                            onClick={stopRecording}
                            variant="destructive"
                            className="rounded-full fade-in absolute right-0"
                            size="icon"
                        >
                            <StopCircle className="w-6 h-6" />
                        </Button>
                    </>
                )}
            </div>
        )
    }
}
